import type { InsightParametersType } from '@/types/dashboard-types'
import { DATA_SOURCE_KEY, isDataSource } from '@/types/data-source'

/**
 * Renders an insight's chosen back end as a query fragment to append to a reference-data
 * request, e.g. the asset lookup behind a caption.
 *
 * Reference data has to come from the same back end as the insight's data — an asset name
 * resolved against Fresh while the rows came from Timescale would paper over exactly the
 * id-mapping mistakes the two-back-end comparison is meant to surface.
 *
 * Returns '' when unset, which resolveApiPath treats as Fresh.
 */
export const dataSourceParam = (parameters: unknown): string => {
  const value = (parameters as Record<string, unknown> | null | undefined)?.[DATA_SOURCE_KEY]
  return isDataSource(value) ? `&${DATA_SOURCE_KEY}=${value}` : ''
}

/**
 * The same choice as an object, to spread into a buildQuery call.
 *
 * For insights that hand buildQuery an explicit whitelist of wire params rather than
 * spreading `parameters`. Those whitelists exist for a reason — they keep client-only
 * parameters such as `periodHours` off the wire — so they can't just be replaced by a
 * spread. But they also drop `dataSource`, and without it the request goes to the
 * configured default back end and the picker appears to do nothing.
 *
 * Returns {} when unset, which resolveApiPath treats as Fresh.
 */
export const dataSourceWireParams = (parameters: unknown): InsightParametersType => {
  const value = (parameters as Record<string, unknown> | null | undefined)?.[DATA_SOURCE_KEY]
  return isDataSource(value) ? { [DATA_SOURCE_KEY]: value } : {}
}
